"use client";
import { useState } from "react";
import type { useFlightSearchForm } from "./hooks/use-flight-search-form";
import { MultiCity } from "./multi-city";
import { OneWay } from "./one-way";
import { RoundTrip } from "./round-trip";
import { InputSelect, useSelect } from "@jetblue/kit/input-select";
import { PassengersInput } from "./components/passengers-input";
import { InputCheckbox } from "@jetblue/kit/input-checkbox";

export type FlightSearchProperties = Pick<
  ReturnType<typeof useFlightSearchForm>,
  | "bestFareFinderBaseUrl"
  | "errors"
  | "formList"
  | "labels"
  | "odQuery"
  | "onTripTypeChange"
  | "passengers"
  | "showDatePickerPricing"
  | "showPromoCode"
  | "tripType"
>;
export const FlightSearch = ({
  bestFareFinderBaseUrl,
  errors,
  formList,
  labels,
  odQuery,
  onTripTypeChange,
  passengers,
  showDatePickerPricing,
  showPromoCode,
  tripType,
}: FlightSearchProperties) => {
  const [isOneWayOpen, setIsOneWayOpen] = useState(false);
  const [isRoundTripOpen, setIsRoundTripOpen] = useState(false);

  return (
    <>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex flex-row items-center gap-6">
          <InputSelect
            aria-label="trip type"
            className="
              text-s-body-extra-bold

              [&_button]:cursor-pointer
            "
            data-fs-element="trip-type"
            label="Trip Type"
            options={[
              { label: labels.tripType.roundTrip, value: "round-trip" },
              { label: labels.tripType.oneWay, value: "one-way" },
              { label: labels.tripType.multiCity, value: "multi-city" },
            ]}
            popoverProperties={{
              containerPadding: 0,
              placement: "bottom start",
              shouldFlip: false,
            }}
            {...useSelect({
              initialSelectedKey: tripType,
              onSelectionChange: (value) => {
                onTripTypeChange(value);
                setIsOneWayOpen(false);
                setIsRoundTripOpen(false);
              },
            })}
            value={tripType}
            variant="simple"
          />
          <PassengersInput labels={labels.passengersDropdown} {...passengers} />
        </div>

        {tripType !== "multi-city" && (
          <InputCheckbox
            className="
              hidden

              md:block
            "
            data-fs-element="points-toggle"
            isSelected={formList.usePoints}
            onChange={() => {
              formList.setUsePoints((current) => !current);
            }}
          >
            {labels.loyaltyCheckbox}
          </InputCheckbox>
        )}
      </div>
      {tripType === "one-way" && (
        <OneWay
          bestFareFinderBaseUrl={bestFareFinderBaseUrl}
          errors={errors}
          formList={formList}
          isOpen={isOneWayOpen}
          labels={labels}
          odQuery={odQuery}
          onOpenChange={setIsOneWayOpen}
          onTripTypeChange={(value) => {
            onTripTypeChange(value);
            setIsOneWayOpen(false);
            setIsRoundTripOpen(true);
          }}
          passengers={{
            adults: passengers.controlAdults.value,
            children: passengers.controlChildren.value,
            infants: passengers.controlInfants.value,
          }}
          showDatePickerPricing={Boolean(showDatePickerPricing)}
          showPromoCode={showPromoCode}
        />
      )}

      {tripType === "round-trip" && (
        <RoundTrip
          bestFareFinderBaseUrl={bestFareFinderBaseUrl}
          errors={errors}
          formList={formList}
          isOpen={isRoundTripOpen}
          labels={labels}
          odQuery={odQuery}
          onOpenChange={setIsRoundTripOpen}
          onTripTypeChange={(value) => {
            onTripTypeChange(value);
            setIsOneWayOpen(true);
            setIsRoundTripOpen(false);
          }}
          passengers={{
            adults: passengers.controlAdults.value,
            children: passengers.controlChildren.value,
            infants: passengers.controlInfants.value,
          }}
          showDatePickerPricing={Boolean(showDatePickerPricing)}
          showPromoCode={showPromoCode}
        />
      )}

      {tripType === "multi-city" && (
        <MultiCity
          errors={errors}
          formList={formList}
          labels={labels}
          odQuery={odQuery}
          passengers={{
            adults: passengers.controlAdults.value,
            children: passengers.controlChildren.value,
            infants: passengers.controlInfants.value,
          }}
        />
      )}
    </>
  );
};
